import type { JSX } from 'preact';
import {
  formatReplayPlaybackMode,
  formatReplayPlaybackRate,
  isReplayPlaybackMode,
  isReplayPlaybackRate,
  REPLAY_PLAYBACK_MODES,
  REPLAY_PLAYBACK_RATES,
  type ReplayPlaybackMode,
  type ReplayPlaybackRate,
} from '../../sim/replay.ts';
import { setPanel } from '../panels.ts';
import './replayControls.css';

export type ReplayControlsState = {
  turnIndex: number;
  turnCount: number;
  moveProgress: number;
  playing: boolean;
  playbackRate: ReplayPlaybackRate;
  /** How many seats this replay has — the playback-mode selector only means anything (and
   * only shows) once there is more than one seat to run concurrently. */
  seatCount: number;
  playbackMode: ReplayPlaybackMode;
  valid: boolean;
};

export type ReplayControlsActions = {
  onBack: () => void;
  onForward: () => void;
  onTogglePlay: () => void;
  onSetPlaybackRate: (rate: ReplayPlaybackRate) => void;
  onSetPlaybackMode: (mode: ReplayPlaybackMode) => void;
  onClose: () => void;
};

export type ReplayControlsProps = {
  state: ReplayControlsState;
  actions: ReplayControlsActions;
};

export type ReplayControls = {
  sync: (state: ReplayControlsState) => void;
  destroy: () => void;
};

export function ReplayControlsPanel({
  state,
  actions,
}: ReplayControlsProps): JSX.Element {
  const atStart = state.turnIndex <= 0 && state.moveProgress <= 0;
  const atEnd = state.turnIndex >= state.turnCount;
  const simultaneous = state.playbackMode === 'simultaneous';
  const unitLabel = simultaneous ? 'Round' : 'Turn';

  return (
    <div
      class="replay-controls"
      aria-label="Replay"
      data-turn-index={state.turnIndex}
      data-turn-count={state.turnCount}
    >
      {state.valid ? (
        <></>
      ) : (
        <p class="replay-controls-error">
          This recording no longer matches the map.
        </p>
      )}
      <div class="replay-controls-row">
        {state.seatCount > 1 ? (
          <label class="replay-controls-mode">
            <span>Playback</span>
            <select
              class="ui-select"
              aria-label="Playback mode"
              value={state.playbackMode}
              onChange={(event) => {
                const next = event.currentTarget.value;

                if (isReplayPlaybackMode(next)) {
                  actions.onSetPlaybackMode(next);
                }
              }}
            >
              {REPLAY_PLAYBACK_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {formatReplayPlaybackMode(mode)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label class="replay-controls-speed">
          <span>Speed</span>
          <select
            class="ui-select"
            aria-label="Playback speed"
            value={String(state.playbackRate)}
            onChange={(event) => {
              const next = Number(event.currentTarget.value);

              if (isReplayPlaybackRate(next)) {
                actions.onSetPlaybackRate(next);
              }
            }}
          >
            {REPLAY_PLAYBACK_RATES.map((rate) => (
              <option key={rate} value={String(rate)}>
                {formatReplayPlaybackRate(rate)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div class="replay-controls-buttons">
        <button
          type="button"
          class="ui-button"
          disabled={!state.valid || atStart}
          onClick={actions.onBack}
        >
          Previous {unitLabel.toLowerCase()}
        </button>
        <button
          type="button"
          class="ui-button"
          disabled={!state.valid || state.turnCount === 0}
          aria-pressed={state.playing ? 'true' : undefined}
          onClick={actions.onTogglePlay}
        >
          {state.playing ? 'Pause' : 'Play'}
        </button>
        <button
          type="button"
          class="ui-button"
          disabled={!state.valid || atEnd}
          onClick={actions.onForward}
        >
          Next {unitLabel.toLowerCase()}
        </button>
        <button type="button" class="ui-button" onClick={actions.onClose}>
          Close replay
        </button>
      </div>
    </div>
  );
}

export function mountReplayControls(
  props: ReplayControlsProps,
): ReplayControls {
  setPanel('replayControls', props);

  return {
    sync: (state) => setPanel('replayControls', { state, actions: props.actions }),
    destroy: () => setPanel('replayControls', null),
  };
}
